import { NgFor } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FdpDetailsSideNavigation } from '@projects/adir/src/app/pages/fdp-details/fdp-details-side-navigation/fdp-details-side-navigation.interface';
import { TailwindMerge } from '@shared/src/interfaces/TwMerge.interface';
import { CardComponent } from '@shared/src/lib/components/card/card.component';
import { StatusChip } from '@shared/src/lib/components/status-chip/status-chip.interface';
import { DynamicIconDirective } from '@shared/src/lib/directives/dynamic-icon.directive';
import { isMpmRole } from '@shared/src/lib/utils/role-util';
import { LocalStorageService } from '@shared/src/services/local-storage.service';
import { Fdp } from '../../../models/fdp.interface';
import { USER_ROLE_ENUM } from '@shared/src/models/user-role.enum';

@Component({
  selector: 'app-fdp-details-side-navigation',
  standalone: true,
  imports: [RouterModule, NgFor, DynamicIconDirective, CardComponent],
  templateUrl: './fdp-details-side-navigation.component.html',
  styleUrl: './fdp-details-side-navigation.component.css',
})
export class FdpDetailsSideNavigationComponent extends TailwindMerge implements OnInit {
  public options: FdpDetailsSideNavigation.SideNavigationOptions = [];
  private requestId: string;

  private _fdpSubmissionRequestStatus = '';
  private userObj: any = undefined;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly localStorageService: LocalStorageService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initControlsBasedOnRole();
    this.route.params.subscribe((params) => {
      if (!params['requestId']) {
        this.router.navigateByUrl('/fdp');
      }
      this.requestId = params['requestId'];
    });
  }

  private initControlsBasedOnRole(): void {
    const userInfo = this.localStorageService.getData('user');
    if (userInfo) {
      this.userObj = JSON.parse(userInfo);
    }
  }

  @Input()
  set fdpSubmissionRequestStatus(value: string) {
    if (value !== this._fdpSubmissionRequestStatus) {
      this._fdpSubmissionRequestStatus = value;
    }
  }
  get fdpSubmissionRequestStatus(): string {
    return this._fdpSubmissionRequestStatus;
  }

  public setupSideMenu(currentWorkflow?: Fdp.FdpDetail.FdpWorkflow): void {
    const encodedFdpRequestId = encodeURIComponent(this.requestId);
    const currentWorkflowStatus = currentWorkflow?.status?.toLowerCase();
    if (currentWorkflowStatus) {
      const isMpmUser = isMpmRole(this.userObj.roleName);
      if (isMpmUser) {
        this.buildSideMenuOptionsForMpmUser(currentWorkflowStatus, encodedFdpRequestId);
      } else {
        this.buildSideMenuOptionsForNonMpmUser(currentWorkflowStatus, encodedFdpRequestId);
      }
    }
  }

  public isCurrentRouteActive(pathname: string) {
    return this.router.url.includes(pathname);
  }

  private buildSideMenuOptionsForMpmUser(currentWorkflowStatus: string, encodedFdpRequestId: string): void {
    let fdpSubmissionLink = '/fdp';
    if (
      currentWorkflowStatus === StatusChip.FdpStatus.DRAFT.toLowerCase() ||
      currentWorkflowStatus === StatusChip.FdpStatus.INITIATE_FDP_SUBMISSION.toLowerCase()
    ) {
      fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/submission`;
    } else if (currentWorkflowStatus === StatusChip.FdpStatus.PENDING_NOMINATION.toLowerCase()) {
      fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/reviewer-nomination`;
    } else if (currentWorkflowStatus === StatusChip.FdpStatus.PENDING_REVIEW.toLowerCase()) {
      if (
        this.userObj?.roleName === USER_ROLE_ENUM.ASSET_MANAGER ||
        this.userObj?.roleName === USER_ROLE_ENUM.TRC_SECRETARIAT
      ) {
        fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/progress-review-approval`;
      } else {
        fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/progress-review`;
      }
    } else if (
      currentWorkflowStatus === StatusChip.FdpStatus.REJECTED.toLowerCase() ||
      currentWorkflowStatus === StatusChip.FdpStatus.POSITION_PAPER_SUBMISSION.toLowerCase()
    ) {
      fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/progress-review-approval`;
    } else if (
      currentWorkflowStatus === StatusChip.FdpStatus.PENDING_ENDORSE.toLowerCase() ||
      currentWorkflowStatus === StatusChip.FdpStatus.PENDING_FINAL_APPROVAL.toLowerCase() ||
      currentWorkflowStatus === StatusChip.FdpStatus.FDP_COMPLETED.toLowerCase()
    ) {
      fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/submission`;
    } else {
      console.log(`TODO - Handle for status: ${currentWorkflowStatus} later`);
    }

    this.options = [
      {
        label: FdpDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.FDP_SUBMISSION,
        link: fdpSubmissionLink,
        iconName: 'file-text',
        isDisabled: false,
      },
      {
        label: FdpDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.POSITION_PAPER,
        link: `/fdp/${this.requestId}/position-paper`,
        iconName: 'clipboard',
        isDisabled: false,
      },
      {
        label: FdpDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.ATTACHMENTS,
        link: `/fdp/${this.requestId}/attachments`,
        iconName: 'attachment',
        isDisabled: false,
      },
      {
        label: FdpDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.ACTIVITY_LOGS,
        link: 'activity-logs',
        iconName: 'time-stopwatch',
        isDisabled: false,
      },
    ];
  }

  private buildSideMenuOptionsForNonMpmUser(currentWorkflowStatus: string, encodedFdpRequestId: string): void {
    let fdpSubmissionLink = '/fdp';
    if (
      currentWorkflowStatus === StatusChip.FdpStatus.DRAFT.toLowerCase() ||
      currentWorkflowStatus === StatusChip.FdpStatus.INITIATE_FDP_SUBMISSION.toLowerCase()
    ) {
      fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/submission`;
    } else if (currentWorkflowStatus === StatusChip.FdpStatus.PENDING_NOMINATION.toLowerCase()) {
      fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/reviewer-nomination`;
    } else if (currentWorkflowStatus === StatusChip.FdpStatus.PENDING_REVIEW.toLowerCase()) {
      if (this.userObj?.roleName === USER_ROLE_ENUM.PAC_REVIEWER) {
        fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/progress-review`;
      } else {
        fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/submission`;
      }
    } else if (currentWorkflowStatus === StatusChip.FdpStatus.POSITION_PAPER_SUBMISSION.toLowerCase()) {
      fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/progress-review-approval`;
    } else if (
      currentWorkflowStatus === StatusChip.FdpStatus.PENDING_ENDORSE.toLowerCase() ||
      currentWorkflowStatus === StatusChip.FdpStatus.PENDING_FINAL_APPROVAL.toLowerCase()
    ) {
      fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/submission`;
    } else if (currentWorkflowStatus === StatusChip.FdpStatus.REJECTED.toLowerCase()) {
      if (this.userObj?.roleName === USER_ROLE_ENUM.PAC_REP) {
        fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/submission`;
      } else {
        fdpSubmissionLink = `/fdp/${encodedFdpRequestId}/progress-review-approval`;
      }
    } else {
      console.log(`TODO - Handle for status: ${currentWorkflowStatus} later`);
    }
    this.options = [
      {
        label: FdpDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.FDP_SUBMISSION,
        link: fdpSubmissionLink,
        iconName: 'file-text',
        isDisabled: false,
      },
      {
        label: FdpDetailsSideNavigation.SIDE_NAVIGATION_OPTION_LABEL.ACTIVITY_LOGS,
        link: 'activity-logs',
        iconName: 'time-stopwatch',
        isDisabled: false,
      },
    ];
  }
}
